import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      <Helmet>
        <title>Privacy Policy - Trademark Venus | How We Protect Your Information</title>
        <meta name="description" content="Read Trademark Venus's privacy policy to understand how we collect, use, and protect your personal information. Your privacy and data security are our top priorities." />
      </Helmet>
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-12">
              Privacy Policy
            </h1>

            <div className="prose prose-slate max-w-none">
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    1. Information We Collect
                  </h2>
                  <p className="text-white leading-relaxed mb-4">
                    We collect information that you provide directly to us when using our trademark registration services. This includes:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-white">
                    <li>Personal identification information (name, email address, phone number)</li>
                    <li>Business information (company name, address, business details)</li>
                    <li>Trademark information (mark details, goods/services descriptions)</li>
                    <li>Payment information (processed securely through our payment processors)</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    2. How We Use Your Information
                  </h2>
                  <p className="text-white leading-relaxed mb-4">
                    We use the information we collect to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-white">
                    <li>Provide, maintain, and improve our trademark registration services</li>
                    <li>Process your trademark applications and communicate with you about your case</li>
                    <li>Send you technical notices, updates, and administrative messages</li>
                    <li>Respond to your inquiries and provide customer support</li>
                    <li>Comply with legal obligations and protect our legal rights</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    3. Information Sharing and Disclosure
                  </h2>
                  <p className="text-white leading-relaxed mb-4">
                    We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-white">
                    <li>With government agencies (such as the USPTO) as required for trademark filing</li>
                    <li>With service providers who assist us in operating our business</li>
                    <li>When required by law or to protect our legal rights</li>
                    <li>With your consent or at your direction</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    4. Data Security
                  </h2>
                  <p className="text-white leading-relaxed">
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    5. Your Rights and Choices
                  </h2>
                  <p className="text-white leading-relaxed mb-4">
                    You have certain rights regarding your personal information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-white">
                    <li>Access and update your personal information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Opt out of marketing communications</li>
                    <li>Request a copy of your information</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    6. Cookies and Tracking Technologies
                  </h2>
                  <p className="text-white leading-relaxed">
                    We use cookies and similar tracking technologies to enhance your experience on our website, analyze usage patterns, and improve our services. You can control cookies through your browser settings.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    7. Children's Privacy
                  </h2>
                  <p className="text-white leading-relaxed">
                    Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    8. Changes to This Policy
                  </h2>
                  <p className="text-white leading-relaxed">
                    We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-white mb-4">
                    9. Contact Us
                  </h2>
                  <p className="text-white leading-relaxed">
                    If you have any questions about this Privacy Policy or our privacy practices, please contact us at info@trademarkvenus.com.
                  </p>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
